import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const locationsData = {
    "North America": [
        {
            id: 1,
            city: "Mississauga, Canada",
            address: "50 Burnhamthorpe Rd W Suite 1201, Mississauga, Ontario L5B 3C2"
        },
        {
            id: 2,
            city: "Mississauga, Canada",
            address: "50 Burnhamthorpe Rd W Suite 1201, Mississauga, Ontario L5B 3C2"
        },
        {
            id: 3,
            city: "Montreal, Canada",
            address: "740 Notre Dame Ouest Suite 1470 Montreal, QC H3C 1J2, Canada"
        },
        {
            id: 4,
            city: "Santa Clara, CA, United States",
            address: "2755 Great America Way, Suite 175, Santa Clara, CA 95054, USA"
        },
        {
            id: 5,
            city: "Atlanta, GA, United States",
            address: "1155 Perimeter Center West, Suite 10-133 Atlanta, GA 30338"
        },
        {
            id: 6,
            city: "Augusta, GA, United States",
            address: "621 NW Frontage Road, Augusta, GA 30907"
        }
    ],
    "LATAM": [
        {
            id: 7,
            city: "São Paulo, Brazil",
            address: "Av. Paulista, 1000 - Bela Vista, São Paulo - SP, 01310-100"
        }
    ],
    "Europe": [
        {
            id: 8,
            city: "London, United Kingdom",
            address: "30 St Mary Axe, London EC3A 8BF, UK"
        }
    ],
    "Middle East": [
        {
            id: 9,
            city: "Dubai, UAE",
            address: " Shtha Tower, Dubai Media City, Dubai"
        }
    ],
    "India": [
        {
            id: 10,
            city: "Pune, India",
            address: "Tech Mahindra Hinjewadi Infotech Park, Phase 3, Pune, 411057"
        }
    ],
    "APAC": [
        {
            id: 11,
            city: "Sydney, Australia",
            address: "Level 21, 201 Elizabeth Street, Sydney, NSW 2000"
        }
    ]
};

const OfficeLocations = () => {
    const [activeTab, setActiveTab] = useState("North America");

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    };

    return (
        <section className="w-full bg-[#ffffff] py-12 sm:py-16 md:py-20 lg:py-24 text-left relative z-10 overflow-hidden">
            <div className="max-w-[1440px] mx-auto w-full px-5 sm:px-10 lg:px-[80px]">

                <div className="w-full mb-10 overflow-x-auto scrollbar-hide flex">
                    <div className="flex gap-6 sm:gap-8 md:gap-10 pb-[1px] shrink-0">
                        {Object.keys(locationsData).map((tab) => {
                            const isActive = activeTab === tab;
                            return (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`relative pb-3 text-[14px] sm:text-[16px] font-graphik font-[500] tracking-wide cursor-pointer transition-colors duration-300 border-none outline-none bg-transparent shrink-0 ${isActive ? "text-[#ED6800]" : "text-[#01386A] hover:text-[#ED6800]"
                                        }`}
                                >
                                    {tab}
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTabLine"
                                            className="absolute bottom-0 left-0 right-0 h-[6px] rounded-full bg-[#ED6800]"
                                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="w-full min-h-[400px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-[28px] w-full items-stretch"
                        >
                            {locationsData[activeTab].map((loc) => (
                                <motion.div
                                    key={loc.id}
                                    variants={cardVariants}
                                    whileHover={{ y: -6, transition: { duration: 0.3 } }}
                                    className="group bg-white relative border border-[#BAC9D7] p-6 sm:p-8 md:p-10 flex flex-col justify-between transition-shadow duration-300 hover:shadow-xl w-full min-h-[260px] sm:min-h-[332px] shrink-0"
                                >
                                    <div className="w-full flex flex-col gap-5">
                                        <h3 className="text-[16px] md:text-[20px] font-[600] text-[#01386A] leading-tight tracking-tight break-words font-sora">
                                            {loc.city}
                                        </h3>
                                    </div>

                                    <div className="mt-6 pt-2 w-full">
                                        <div className="flex flex-col gap-1 pb-[26px] w-full">
                                            <span className="text-[16px] sm:text-[20px] font-[600] font-sora text-[#49577A]">
                                                Address
                                            </span>
                                            <p className="text-[14px] md:text-[16px] text-[#49577A] leading-relaxed break-words font-[500] font-graphik">
                                                {loc.address}
                                            </p>
                                        </div>
                                        <a
                                            href={`https://maps.google.com/?q=${encodeURIComponent(loc.city + " " + loc.address)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center gap-2 border border-[#ED6800] text-[#ED6800] text-[14px] sm:text-[16px] font-[500] font-graphik px-5 py-2.5 transition-colors duration-300 hover:bg-[#ED6800] hover:text-white group select-none outline-none bg-transparent"
                                        >
                                            <span>Get Direction</span>
                                            <span className="transition-transform duration-300 group-hover:translate-x-1 text-[15px]">&gt;</span>
                                        </a>
                                    </div>

                                    <div className="h-[4px] w-full absolute bottom-0 bg-[#01386A] left-0 overflow-hidden">
                                        <div className="h-full w-0 bg-[#ED6800] transition-all duration-500 ease-out group-hover:w-full"></div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>

            </div>
        </section>
    );
};

export default OfficeLocations;